import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ArrowRight, Home, Settings } from "lucide-react";
import { NavLink } from "react-router";
import { useState, useEffect } from "react";
import { Calendar } from "./Calendar";

import "../index.css";
import { useMoodOptions } from "@/hooks/use-mood-options";

export function Board() {
  const [month, setMonth] = useState(new Date().getMonth());
  const { moodOptions } = useMoodOptions();

  const changeMonth = (next: boolean) => {
    if (!next && month === 0 || next && month === 11) {
      return;
    }

    if (next) {
      setMonth(month + 1);
    } else {
      setMonth(month - 1);
    }
  }

  type DayData = { color?: string; comment?: string };
  const [moods, setMoods] = useState<Record<string, DayData>>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("moodboard-data");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const migrated: Record<string, DayData> = {};
          Object.entries(parsed).forEach(([key, value]) => {
            if (typeof value === "string") {
              migrated[key] = { color: value };
            } else {
              migrated[key] = value as DayData;
            }
          });
          return migrated;
        } catch (e) {
          console.error("Failed to parse moodboard data", e);
        }
      }
    }
    return {};
  });

  const [monthlyComments, setMonthlyComments] = useState<Record<string, string>>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("moodboard-monthly-comments");
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error("Failed to parse monthly comments", e);
        }
      }
    }
    return {};
  });

  useEffect(() => {
    localStorage.setItem("moodboard-data", JSON.stringify(moods));
  }, [moods]);

  useEffect(() => {
    localStorage.setItem("moodboard-monthly-comments", JSON.stringify(monthlyComments));
  }, [monthlyComments]);

  const handleMoodSelect = (day: number, moodColor: string) => {
    const dateKey = `${2026}-${month}-${day}`;
    setMoods((prev) => ({
      ...prev,
      [dateKey]: { ...prev[dateKey], color: moodColor },
    }));
  };

  const handleDayCommentChange = (day: number, comment: string) => {
    const dateKey = `${2026}-${month}-${day}`;
    setMoods((prev) => ({
      ...prev,
      [dateKey]: { ...prev[dateKey], comment },
    }));
  };

  const handleMonthlyCommentChange = (text: string) => {
    const monthKey = `${2026}-${month}`;
    setMonthlyComments((prev) => ({
      ...prev,
      [monthKey]: text,
    }));
  }

  const getMoodCount = (color: string) => {
    let count = 0;
    Object.entries(moods).forEach(([dateStr, moodVal]) => {
      const parts = dateStr.split("-");
      if (parts.length >= 2) {
        const y = parts[0];
        const m = parts[1];
        if (y === "2026" && m === month.toString() && moodVal.color?.includes(color?.split(" ")[0] ?? "")) {
          count++;
        }
      }
    });
    return count;
  }

  return (
    <div className="container mx-auto p-8 relative z-10">
      <div className="flex items-center gap-4 mb-8">
        <div className="mb-title text-4xl!">Mon Moodboard</div>
        <Button variant="ghost" size="lg" asChild className="shrink-0">
          <NavLink to="/settings">
            <Settings /> Paramètres
          </NavLink>
        </Button>
      </div>

      <div className="bg-card border rounded-lg p-4 shadow-sm text-center lg:w-[30%] lg:inline-block lg:mr-[2%] lg:align-top">
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="icon" onClick={() => changeMonth(false)}>
            <ArrowLeft />
          </Button>
          <span className="capitalize">
            {new Date(0, month).toLocaleString("fr-FR", { month: "long" })} 2026
          </span>
          <Button variant="ghost" size="icon" onClick={() => changeMonth(true)}>
            <ArrowRight />
          </Button>
        </div>
        <div className="mt-8 space-y-4">
          {moodOptions.map((mood) => (
            <div key={mood.label} className="flex items-center justify-between px-4">
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full ${mood.color.split(" ")[0]}`} />
                <span className="text-sm font-medium">{mood.label}</span>
              </div>
              <span className="text-sm font-bold text-muted-foreground">{getMoodCount(mood.color)} {getMoodCount(mood.color) <= 1 ? "jour" : "jours"}</span>
            </div>
          ))}
        </div>

        <div className="mt-8 px-4 text-left space-y-2">
          <span className="text-sm font-medium text-muted-foreground">Commentaire du mois</span>
          <Textarea
            placeholder="Aucun commentaire pour le moment"
            className="resize-y h-32"
            value={monthlyComments[`2026-${month}`] || ""}
            onChange={(e) => handleMonthlyCommentChange(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-card border rounded-lg p-4 shadow-sm text-center lg:w-[68%] lg:inline-block mt-4 lg:mt-0 lg:align-top">
        <Calendar
          month={month}
          year={2026}
          moods={moods}
          moodOptions={moodOptions}
          onMoodSelect={handleMoodSelect}
          onDayCommentChange={handleDayCommentChange}
        />
      </div>
    </div>
  );
}